import { IAuthAccount } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { IMailSendPayload } from "../src/taskpane/interfaces/IMailSendPayload";
import { detectEdgeOrSafari } from "./BrowserUtils";
import { IMailSettingsDto } from "./OutlookUtils";
import LZString = require("lz-string");
import { logger } from "./logger";
import { apiGetPayload, apiSavePayload } from "../src/taskpane/services/ApiService";

export enum MailSettingsStorage {
    Local,
    Roaming
}

export enum MailSettingsKeys {
    RememberSettings = "sos",
    DialogPayload = "dpld"
}

export enum DialogEvents {
    OpenTaskpane = "Open Taskpane",
    StartAuthFlow = "Initiate Auth Flow",
}

export interface IMailSettings {
    saveMail: boolean,
}

export const SaveMailSettings = async (settings: IMailSettings, account: IAuthAccount): Promise<void> => {
    await trySaveMailSettings(MailSettingsStorage.Local, settings, account);
}

const trySaveMailSettings = async (storage: MailSettingsStorage, settings: IMailSettings, account: IAuthAccount): Promise<boolean> => {
    try {
        if (storage === MailSettingsStorage.Roaming && Office.context.requirements.isSetSupported("Mailbox")) {
            Office.context.roamingSettings.set(`${MailSettingsKeys.RememberSettings}.${account.accountId}`, settings);

            return await new Promise((resolve, reject) => {
                Office.context.roamingSettings.saveAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(true);
                    } else {
                        console.error(`Roaming AuthState persistence failed with message: ${result.error.message}`);
                        reject(false);
                    }
                });
            });
        }
        else if (storage === MailSettingsStorage.Local) {
            localStorage.setItem(`${MailSettingsKeys.RememberSettings}.${account.accountId}`, JSON.stringify(settings));
            return true;
        }

    } catch (error) {
        console.error("AuthState persistence failed for storage type: " + MailSettingsStorage[storage]);
        logger(error, "SaveOnSendUtils.ts (trySaveMailSettings)");
    }

    return false;
}

export const getMailSettings = (accountId: string): IMailSettings => {

    try {
        const settings = JSON.parse(localStorage.getItem(`${MailSettingsKeys.RememberSettings}.${accountId}`)) as IMailSettings;
        return settings;
    } catch (e) {
        logger(e, "SaveOnSendUtils.ts (getMailSettings)");
    }

    return null;
}

export const setDialogPayload = async (mailToSendPayload: IMailSendPayload): Promise<void> => {
    try {
        const encryptedPayload = LZString.compressToUTF16(JSON.stringify(mailToSendPayload));

        if (Office.context.diagnostics.platform === Office.PlatformType.OfficeOnline
            && detectEdgeOrSafari()) {
            const mailPayloadDto: IMailSettingsDto = {
                accountId: mailToSendPayload.accountId,
                encryptedPayload: encryptedPayload,
            }
            await apiSavePayload(mailPayloadDto);
        }
        else {
            localStorage.setItem(`${MailSettingsKeys.DialogPayload}.${mailToSendPayload.accountId}`, encryptedPayload);
        }
    } catch (e) {
        logger(e, "SaveOnSendUtils.ts (setDialogPayload)");
    }
}

export const getDialogPayload = async (accountId: string): Promise<IMailSendPayload> => {
    let mailPayload: IMailSendPayload;
    try {
        if (Office.context.diagnostics.platform === Office.PlatformType.OfficeOnline
            && detectEdgeOrSafari()) {
            mailPayload = JSON.parse(LZString.decompressFromUTF16(await apiGetPayload(accountId)));
        }
        else {
            const storageKey: string = `${MailSettingsKeys.DialogPayload}.${accountId}`;
            mailPayload = JSON.parse(LZString.decompressFromUTF16(localStorage.getItem(storageKey))) as IMailSendPayload;
            localStorage.removeItem(storageKey);
        }
    } catch (e) {
        logger(e, "SaveOnSendUtils.ts (getDialogPayload)");
    }
    return mailPayload;
}

export const tryOpenDialog = async (dialogOpenRequest: () => Promise<void>): Promise<void> => {
    for (let attempt = 0; attempt < 5; ++attempt) {
        await new Promise(resolve => setTimeout(resolve, 150));
        try {
            await dialogOpenRequest();
            return;
        } catch (error) {
            //Only retry on: 12007 The operation failed because this add-in already has an active dialog
            if (error.code !== 12007) {
                throw error;
            }
        }
    }
    throw new Error("Dialog could not be opened");
}